import { useMemo, useState } from "react";
import {
  Alert, Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Stack, Switch, TextField, Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "../../api/client";

interface RoleCatalogItem { id: string; key: string; name: string; module: { id: string; key: string; name: string } }
interface GroupCatalogItem { id: string; key: string; name: string; institutionId: string | null; roleCount: number }
interface LookupResult { found: boolean; name?: string; alreadyMember?: boolean }

interface Props {
  institutionId: string;
  onClose: () => void;
  onDone: (result: { kind: "member" | "invite" }) => void;
}

/**
 * Associa um usuário à instituição por e-mail. Faz lookup: se o e-mail já
 * existe entre os cadastrados → modo "Associar" (ingresso imediato); senão →
 * modo "Convidar" (envia link de auto-registro). Papéis/grupos são opcionais.
 */
export function AssociateMemberDialog({ institutionId, onClose, onDone }: Props) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [roleIds, setRoleIds] = useState<RoleCatalogItem[]>([]);
  const [groupIds, setGroupIds] = useState<GroupCatalogItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());

  // Lookup escopado por instituição (admin não enxerga /users global): decide
  // associar (existe) × convidar (não existe) e sinaliza se já é membro.
  const lookupQ = useQuery<LookupResult>({
    queryKey: ["member-lookup", institutionId, email.trim().toLowerCase()],
    queryFn: async () =>
      (await api.get<LookupResult>(`/institutions/${institutionId}/user-lookup`, { params: { email: email.trim() } })).data,
    enabled: emailValid,
  });
  const found = lookupQ.data?.found ? lookupQ.data : null;
  const alreadyMember = !!lookupQ.data?.alreadyMember;
  const isInvite = emailValid && !lookupQ.isFetching && lookupQ.data != null && !lookupQ.data.found;

  const rolesQ = useQuery<RoleCatalogItem[]>({
    queryKey: ["roles-all-associate"],
    queryFn: async () => {
      const mods = (await api.get<{ id: string; active?: boolean }[]>("/modules")).data ?? [];
      const all: RoleCatalogItem[] = [];
      for (const md of mods.filter((m) => m.active !== false)) {
        const rs = await api.get<RoleCatalogItem[]>("/roles", { params: { module_id: md.id } });
        all.push(...rs.data);
      }
      return all;
    },
  });
  const groupsQ = useQuery<GroupCatalogItem[]>({
    queryKey: ["groups-associate", institutionId],
    queryFn: async () => {
      const [global, local] = await Promise.all([
        api.get<{ data: GroupCatalogItem[] }>("/role-groups", { params: { scope: "global", page_size: 100 } }),
        api.get<{ data: GroupCatalogItem[] }>("/role-groups", { params: { scope: institutionId, page_size: 100 } }),
      ]);
      return [...global.data.data, ...local.data.data];
    },
  });

  const submitM = useMutation({
    mutationFn: () => api.post<{ kind: "member" | "invite" }>(`/institutions/${institutionId}/members`, {
      email: email.trim(),
      name: name.trim() || undefined,
      isAdmin,
      roleIds: roleIds.map((r) => r.id),
      roleGroupIds: groupIds.map((g) => g.id),
    }),
    onSuccess: (res) => onDone(res.data),
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? "Não foi possível associar o usuário.");
    },
  });

  const canSubmit = useMemo(
    () => emailValid && !alreadyMember && (found ? true : name.trim().length >= 2) && !submitM.isPending && !lookupQ.isFetching,
    [emailValid, alreadyMember, found, name, submitM.isPending, lookupQ.isFetching],
  );

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm"
      slotProps={{ paper: { component: "form", onSubmit: (e: React.FormEvent) => { e.preventDefault(); if (canSubmit) submitM.mutate(); } } }}>
      <DialogTitle>Associar usuário</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="E-mail" type="email" required fullWidth value={email}
            onChange={(e) => { setEmail(e.target.value); setError(null); }} autoFocus />

          {emailValid && lookupQ.isFetching && <Typography variant="caption" color="text.secondary">Verificando…</Typography>}
          {found && alreadyMember && (
            <Alert severity="warning"><strong>{found.name}</strong> já é membro ativo desta instituição.</Alert>
          )}
          {found && !alreadyMember && (
            <Alert severity="success">Usuário encontrado: <strong>{found.name}</strong>. Será associado imediatamente como ativo.</Alert>
          )}
          {isInvite && (
            <>
              <Alert severity="info">Nenhum usuário com esse e-mail. Um convite de auto-registro será enviado.</Alert>
              <TextField label="Nome do convidado" required fullWidth value={name} onChange={(e) => setName(e.target.value)} />
            </>
          )}

          <FormControlLabel
            control={<Switch checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} />}
            label="Administrador da instituição"
          />

          <Autocomplete<RoleCatalogItem, true>
            multiple options={rolesQ.data ?? []} value={roleIds}
            loading={rolesQ.isFetching}
            getOptionLabel={(o) => `${o.module.key} / ${o.name}`}
            groupBy={(o) => o.module.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, v) => setRoleIds(v)}
            renderInput={(p) => <TextField {...p} label="Papéis (opcional)" size="small" />}
          />
          <Autocomplete<GroupCatalogItem, true>
            multiple options={groupsQ.data ?? []} value={groupIds}
            loading={groupsQ.isFetching}
            getOptionLabel={(o) => `${o.name}${o.institutionId ? "" : " (global)"}`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, v) => setGroupIds(v)}
            renderInput={(p) => <TextField {...p} label="Grupos de papéis (opcional)" size="small" />}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button type="submit" variant="contained" disabled={!canSubmit}>
          {isInvite ? "Enviar convite" : "Associar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
